import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createCommentVNode as _createCommentVNode, vModelText as _vModelText } from "vue"

const _hoisted_1 = { class: "block w-screen px-4 md:w-full text-sm" }
const _hoisted_2 = { class: "flex items-center w-full" }
const _hoisted_3 = { class: "flex-grow flex items-center" }
const _hoisted_4 = { class: "text-black font-bold pr-1 text-xs" }
const _hoisted_5 = { class: "font-light text-xs" }
const _hoisted_6 = {
  key: 0,
  class: "space-y-4 my-2"
}
const _hoisted_7 = {
  key: 0,
  class: "w-full"
}
const _hoisted_8 = ["value"]
const _hoisted_9 = { class: "relative" }
const _hoisted_10 = ["title"]
const _hoisted_11 = {
  key: 0,
  xmlns: "http://www.w3.org/2000/svg",
  fill: "none",
  viewBox: "0 0 24 24",
  "stroke-width": "1.5",
  stroke: "currentColor",
  class: "h-5 w-5"
}
const _hoisted_12 = {
  key: 1,
  xmlns: "http://www.w3.org/2000/svg",
  fill: "none",
  viewBox: "0 0 24 24",
  "stroke-width": "1.5",
  stroke: "currentColor",
  class: "h-5 w-5"
}

import {computed, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";
import {useRoute} from "vue-router";
import {useI18n} from "vue-i18n";

// Emits a generic patch event, handled by parent (itemV2Main.vue)
const pageStr = "components.itemV2.itemV2Name"

// Access the session from the store (keeps consistency with the rest of the app)

export default /*@__PURE__*/_defineComponent({
  __name: 'itemV2Name',
  emits: ["patch"],
  setup(__props, { emit: __emit }) {

const emit = __emit;

const {t} = useI18n();
const store = useStore();
const route = useRoute();
const session = ref(store.getters["session/get"]);

// Current item index from the route (fallback to 0)
const itemIdx = computed(() => {
  const raw = route.params.itemIdx;
  if (typeof raw === "string") {
    const n = parseInt(raw, 10);
    return isNaN(n) ? 0 : n;
  }
  if (typeof raw === "number") return raw;
  return 0;
});

// Determine selected person
// Prefer a person already associated with the current item; otherwise default to the first person
const selectedPersonIdx = ref(0);

const persons = computed(() => session.value?.persons ?? []);

onMounted(() => {
  if (persons.value.length === 0) return;
  const idx = persons.value.findIndex((p: any) => Array.isArray(p.items) && p.items.includes(itemIdx.value));
  selectedPersonIdx.value = idx >= 0 ? idx : 0;
  ensureItemIncluded();
});

watch(selectedPersonIdx, () => {
  ensureItemIncluded();
});

// The editable name bound to the input
const nameModel = ref("");

// Keep nameModel in sync with selected person
watch(
    () => [selectedPersonIdx.value, persons.value?.[selectedPersonIdx.value]?.name],
    () => {
      const p = persons.value?.[selectedPersonIdx.value];
      nameModel.value = p?.name ?? "";
    },
    {immediate: true}
);

// Ensure the current item is listed for the selected person (frontend side)
const ensureItemIncluded = () => {
  const p = persons.value?.[selectedPersonIdx.value];
  if (!p) return;
  if (!Array.isArray(p.items)) p.items = [];
  if (!p.items.includes(itemIdx.value)) {
    // Update locally so the UI reflects the association immediately
    p.items.push(itemIdx.value);
    // Persist the association via patch so the backend knows this item belongs to the person
    emit('patch', { action: `person.${selectedPersonIdx.value}.items`, value: p.items, certificated_diamond: true });
  }
};

// Emit patch on change as specified: action "person.<person_id>.name" with the name value
const commitName = () => {
  const personId = selectedPersonIdx.value;
  let value = (nameModel.value || "").trim();
  if (!value) {
    // If name is empty, store default name: "Item <person_id>"
    value = t(pageStr + '.person_n', {n: personId + 1});
    // Reflect the default in the UI immediately
    nameModel.value = value;
  }
  emit('patch', { action: `person.${personId}.name`, value, certificated_diamond: true });
};

// Controls expand/collapse like itemV2VariantsList "closed" view
const showValues = ref(false);

// Determine whether current input is a new name not present anywhere in the session
const currentPersonName = computed(() => persons.value?.[selectedPersonIdx.value]?.name ?? "");
const normalizeName = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();
const sessionNamesSet = computed(() => new Set((persons.value || []).map((p: any) => normalizeName(p?.name))));
const isDirty = computed(() => normalizeName(nameModel.value) !== normalizeName(currentPersonName.value));
const nameInSession = computed(() => sessionNamesSet.value.has(normalizeName(nameModel.value)));
const showSaveIcon = computed(() => isDirty.value && !nameInSession.value);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("span", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (showValues.value = !showValues.value)),
          class: "cursor-pointer"
        }, [
          _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(t)(pageStr + '.article_for')), 1),
          _createElementVNode("span", _hoisted_5, _toDisplayString(nameModel.value), 1)
        ])
      ]),
      _createElementVNode("div", {
        class: "flex-shrink-0 w-6 cursor-pointer text-brand-main",
        onClick: _cache[1] || (_cache[1] = ($event: any) => (showValues.value = !showValues.value))
      }, [
        (_openBlock(), _createElementBlock("svg", {
          xmlns: "http://www.w3.org/2000/svg",
          class: _normalizeClass(["w-4 h-4 transition-transform duration-300", { 'rotate-180': showValues.value }]),
          fill: "none",
          viewBox: "0 0 24 24",
          stroke: "currentColor"
        }, [...(_cache[4] || (_cache[4] = [
          _createElementVNode("path", {
            "stroke-linecap": "round",
            "stroke-linejoin": "round",
            "stroke-width": "2",
            d: "M19 9l-7 7-7-7"
          }, null, -1)
        ]))], 2))
      ])
    ]),
    (showValues.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          (persons.value.length > 1)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _withDirectives(_createElementVNode("select", {
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedPersonIdx).value = $event)),
                  class: "text-sm block w-full bg-white p-2 border border-brand-main"
                }, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(persons.value, (p, idx) => {
                    return (_openBlock(), _createElementBlock("option", {
                      key: 'p'+idx,
                      value: idx
                    }, _toDisplayString(_unref(t)(pageStr + '.person_idx_name', { idx: idx + 1, name: p.name })), 9, _hoisted_8))
                  }), 128))
                ], 512), [
                  [
                    _vModelSelect,
                    selectedPersonIdx.value,
                    void 0,
                    { number: true }
                  ]
                ])
              ]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_9, [
            _withDirectives(_createElementVNode("input", {
              type: "text",
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((nameModel).value = $event)),
              onChange: commitName,
              class: "text-base block w-full bg-white p-2 pr-12 border border-brand-main"
            }, null, 544), [
              [_vModelText, nameModel.value]
            ]),
            _createElementVNode("button", {
              type: "button",
              class: "absolute inset-y-0 right-2 my-auto h-8 w-8 flex items-center justify-center text-brand-main hover:text-white hover:bg-brand-main rounded",
              onClick: commitName,
              title: showSaveIcon.value ? _unref(t)(pageStr + '.save_new_name') : _unref(t)(pageStr + '.saved')
            }, [
              (showSaveIcon.value)
                ? (_openBlock(), _createElementBlock("svg", _hoisted_11, [...(_cache[5] || (_cache[5] = [
                    _createElementVNode("path", {
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      d: "M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5"
                    }, null, -1),
                    _createElementVNode("path", {
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      d: "M7.5 10.5L12 15l4.5-4.5M12 15V3"
                    }, null, -1)
                  ]))]))
                : (_openBlock(), _createElementBlock("svg", _hoisted_12, [...(_cache[6] || (_cache[6] = [
                    _createElementVNode("path", {
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      d: "M4.5 12.75l6 6 9-13.5"
                    }, null, -1)
                  ]))]))
            ], 8, _hoisted_10)
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})